import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Search, Plus, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, AlertTriangle, CalendarClock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtTime = (iso) => { if (!iso) return '—'; const d = new Date(iso); return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
const fmtQty = (n) => (n == null || isNaN(n)) ? '—' : (+n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtMoney = (n) => (n == null || isNaN(n)) ? '—' : '₹' + Math.round(n).toLocaleString('en-IN');
const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Focused, single-purpose entry screen for the 'stores' role: log a
 * receipt or issue for an item directly, no Excel needed. Route is
 * guarded by StoresRoute in App.js — only role='stores' or 'admin' can
 * reach it. Each submit hits POST /api/general-stores/transactions, which
 * updates the running FIFO balance immediately (see general_stores/live.py) —
 * the General Stores dashboard's KPIs/items table reflect it on next load,
 * no rebuild step needed the way an Excel upload requires.
 *
 * Backdating (logging an entry for a past date instead of "right now") is
 * admin-only — a plain 'stores' user never sees the date field and always
 * logs as of the current moment. The backend enforces this independently
 * (rejects entry_date from a non-admin), this is just the UI reflecting
 * that a stores-role person shouldn't be able to rewrite past history.
 */
const StoresEntryPage = () => {
  const { isAdmin, isStores } = useAuth();
  // Backdate window: admin unlimited, stores capped at 3 days. Anyone
  // else with the URL (viewer, etc.) can only log today. The min= attr
  // on the date input mirrors the backend cap in router.py — keep in sync.
  const canBackdate = isAdmin || isStores;
  const minEntryDate = (() => {
    if (isAdmin) return undefined; // no floor
    if (isStores) {
      const d = new Date();
      d.setDate(d.getDate() - 3);
      return d.toISOString().slice(0, 10);
    }
    return todayStr();
  })();
  const [kind, setKind] = useState('issue'); // 'receipt' | 'issue'
  const [entryDate, setEntryDate] = useState(todayStr());
  const [itemQuery, setItemQuery] = useState('');
  const [itemOptions, setItemOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null); // {code, name, unit} or null
  const [showAddNew, setShowAddNew] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemUnit, setNewItemUnit] = useState('');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {ok, message}
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const searchTimer = useRef(null);
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadActivity = useCallback(async () => {
    try {
      setLoadingActivity(true);
      const res = await axios.get(`${API}/api/general-stores/transactions`, { withCredentials: true });
      setActivity(res.data.transactions || []);
    } catch (e) {
      // non-fatal — the entry form still works without the log loading
    } finally {
      setLoadingActivity(false);
    }
  }, []);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!itemQuery.trim() || selectedItem) { setItemOptions([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/general-stores/items`, { params: { q: itemQuery }, withCredentials: true });
        setItemOptions(res.data.items || []);
        setShowDropdown(true);
      } catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [itemQuery, selectedItem]);

  const pickItem = (item) => {
    setSelectedItem(item);
    setItemQuery(`${item.code} — ${item.name}`);
    setShowDropdown(false);
    setShowAddNew(false);
  };

  const clearItem = () => {
    setSelectedItem(null);
    setItemQuery('');
    setItemOptions([]);
    setShowAddNew(false);
  };

  const resetForm = () => {
    clearItem();
    setQty('');
    setUnitCost('');
    setNote('');
    setNewItemName('');
    setNewItemUnit('');
    // Deliberately NOT resetting entryDate — an admin backfilling several
    // entries for the same past day would otherwise have to re-pick the
    // date after every single submit.
  };

  const canSubmit = (selectedItem || (showAddNew && itemQuery.trim() && newItemName.trim()))
    && qty && +qty > 0
    && (kind === 'issue' || (unitCost && +unitCost >= 0));

  const isBackdated = canBackdate && entryDate !== todayStr();

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setFeedback(null);
    const item_code = selectedItem ? selectedItem.code : itemQuery.trim();
    try {
      const res = await axios.post(`${API}/api/general-stores/transactions`, {
        item_code,
        kind,
        qty: +qty,
        unit_cost: kind === 'receipt' ? +unitCost : null,
        note: note.trim() || null,
        new_item_name: !selectedItem ? (newItemName.trim() || item_code) : null,
        new_item_unit: !selectedItem ? (newItemUnit.trim() || null) : null,
        entry_date: canBackdate ? entryDate : undefined,
      }, { withCredentials: true });
      const when = isBackdated ? ` for ${entryDate}` : '';
      const recomputeNote = res.data?.recomputed_dates?.length
        ? ` History recomputed through ${res.data.recomputed_dates[res.data.recomputed_dates.length - 1]}.`
        : '';
      setFeedback({ ok: true, message: `Logged ${kind === 'receipt' ? 'receipt' : 'issue'} of ${qty} for ${item_code}${when}.${recomputeNote}` });
      resetForm();
      loadActivity();
    } catch (e) {
      setFeedback({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="stores-entry-page">
      <div>
        <h1 className="text-xl font-bold text-dash-ink">Log Stock Entry</h1>
        <p className="text-sm text-dash-mut mt-1">Record a receipt or issue directly — no Excel file needed. The General Stores dashboard's balance updates as soon as you submit.</p>
      </div>

      <div className="bg-dash-panel border border-dash-line rounded-xl p-5 space-y-4">
        {/* Receipt / Issue toggle */}
        <div className="flex gap-1 bg-dash-panel2 border border-dash-line rounded-lg p-1 w-fit">
          <button
            onClick={() => setKind('issue')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition ${kind === 'issue' ? 'bg-dash-panel text-dash-ink font-semibold shadow-sm' : 'text-dash-mut hover:text-dash-ink'}`}
            data-testid="kind-issue"
          >
            <ArrowUpFromLine className="w-3.5 h-3.5" /> Issue
          </button>
          <button
            onClick={() => setKind('receipt')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition ${kind === 'receipt' ? 'bg-dash-panel text-dash-ink font-semibold shadow-sm' : 'text-dash-mut hover:text-dash-ink'}`}
            data-testid="kind-receipt"
          >
            <ArrowDownToLine className="w-3.5 h-3.5" /> Receipt
          </button>
        </div>

        {/* Date — admin sees an unrestricted picker; stores users see a
            picker capped at 3 days back (matches router.py's server-side
            check). Anyone else with the URL doesn't see the picker at all. */}
        {canBackdate && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Date</label>
            <div className="relative max-w-[180px]">
              <CalendarClock className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dash-mut" />
              <input
                type="date" value={entryDate} max={todayStr()} min={minEntryDate}
                onChange={(e) => setEntryDate(e.target.value || todayStr())}
                className="w-full bg-dash-panel2 border border-dash-line rounded-lg pl-8 pr-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                data-testid="entry-date-input"
              />
            </div>
            {isStores && !isAdmin && (
              <div className="mt-1.5 text-[11px] text-dash-mut">
                Stores users can backdate up to 3 days. Older entries need admin.
              </div>
            )}
            {isBackdated && (
              <div className="mt-1.5 text-[11px] text-dash-coral flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Backdating to {entryDate} — this recomputes history from that day forward.
              </div>
            )}
          </div>
        )}

        {/* Item picker */}
        <div className="relative" ref={dropdownRef}>
          <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Item</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dash-mut" />
            <input
              type="text"
              value={itemQuery}
              onChange={(e) => { setItemQuery(e.target.value); setSelectedItem(null); }}
              onFocus={() => itemOptions.length > 0 && setShowDropdown(true)}
              placeholder="Search item code or name..."
              className="w-full bg-dash-panel2 border border-dash-line rounded-lg pl-8 pr-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
              data-testid="item-search-input"
            />
          </div>
          {showDropdown && itemOptions.length > 0 && !selectedItem && (
            <div className="absolute z-20 mt-1 w-full bg-dash-panel border border-dash-line rounded-lg shadow-xl max-h-56 overflow-auto">
              {itemOptions.map((it) => (
                <button
                  key={it.code}
                  onClick={() => pickItem(it)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-dash-panel2 border-b border-dash-line last:border-b-0"
                >
                  <div className="font-mono text-dash-ink">{it.code}</div>
                  <div className="text-dash-mut truncate">{it.name}{it.unit ? ` · ${it.unit}` : ''}</div>
                </button>
              ))}
            </div>
          )}
          {itemQuery.trim() && !selectedItem && itemOptions.length === 0 && (
            <div className="mt-2">
              {!showAddNew ? (
                <button onClick={() => setShowAddNew(true)} className="text-xs text-dash-ice inline-flex items-center gap-1 hover:underline" data-testid="show-add-new-item">
                  <Plus className="w-3.5 h-3.5" /> "{itemQuery.trim()}" isn't in the item list — add it as a new item
                </button>
              ) : (
                <div className="bg-dash-panel2 border border-dash-line rounded-lg p-3 space-y-2 mt-1">
                  <div className="text-[11px] text-dash-mut">New item code: <span className="font-mono text-dash-ink">{itemQuery.trim()}</span></div>
                  <input
                    type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Item name (required)"
                    className="w-full bg-dash-panel border border-dash-line rounded px-2.5 py-1.5 text-xs text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    data-testid="new-item-name-input"
                  />
                  <input
                    type="text" value={newItemUnit} onChange={(e) => setNewItemUnit(e.target.value)}
                    placeholder="Unit (optional, e.g. unit(s), kg, Ltr)"
                    className="w-full bg-dash-panel border border-dash-line rounded px-2.5 py-1.5 text-xs text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                    data-testid="new-item-unit-input"
                  />
                </div>
              )}
            </div>
          )}
          {selectedItem && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-dash-mut">
              <CheckCircle2 className="w-3.5 h-3.5 text-dash-green" /> {selectedItem.unit || 'no unit on record'}
              <button onClick={clearItem} className="text-dash-ice hover:underline ml-1">change</button>
            </div>
          )}
        </div>

        {/* Qty / Unit cost */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Quantity</label>
            <input
              type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)}
              placeholder="0"
              className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
              data-testid="qty-input"
            />
          </div>
          {kind === 'receipt' && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Unit cost (₹)</label>
              <input
                type="number" min="0" step="any" value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
                placeholder="0.00"
                className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
                data-testid="unit-cost-input"
              />
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold mb-1 block">Note (optional)</label>
          <input
            type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={kind === 'issue' ? 'e.g. issued to Maintenance for pump repair' : 'e.g. PO #4821, vendor delivery'}
            className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-sm text-dash-ink focus:outline-none focus:ring-2 focus:ring-dash-ice"
            data-testid="note-input"
          />
        </div>

        {feedback && (
          <div className={`rounded-lg px-3 py-2 text-xs border flex items-start gap-1.5 ${feedback.ok ? 'border-dash-green/30 bg-[rgba(107,203,119,.08)] text-dash-ink' : 'border-dash-coral/30 bg-[rgba(255,107,138,.08)] text-dash-coral'}`}>
            {feedback.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            {feedback.message}
          </div>
        )}

        <button
          onClick={submit}
          disabled={!canSubmit || submitting}
          className="w-full bg-dash-ice text-white px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="submit-transaction"
        >
          {submitting ? 'Logging…' : `Log ${kind === 'receipt' ? 'Receipt' : 'Issue'}`}
        </button>
      </div>

      {/* Today's activity */}
      <div className="bg-dash-panel border border-dash-line rounded-xl p-4">
        <h3 className="text-sm font-semibold text-dash-ink mb-3">Today's activity</h3>
        {loadingActivity ? (
          <div className="text-xs text-dash-mut italic py-4 text-center">Loading…</div>
        ) : activity.length === 0 ? (
          <div className="text-xs text-dash-mut italic py-4 text-center">Nothing logged yet today.</div>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-auto">
            {activity.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between gap-3 bg-dash-panel2 border border-dash-line rounded-lg px-3 py-2 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  {tx.kind === 'receipt' ? <ArrowDownToLine className="w-3.5 h-3.5 text-dash-green shrink-0" /> : <ArrowUpFromLine className="w-3.5 h-3.5 text-dash-coral shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-dash-ink font-mono truncate">{tx.item_code} {tx.item_name ? `— ${tx.item_name}` : ''}</div>
                    {tx.note && <div className="text-dash-mut truncate">{tx.note}</div>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-dash-ink font-mono">{fmtQty(tx.qty)}{tx.unit ? ` ${tx.unit}` : ''}</div>
                  <div className="text-dash-mut">{tx.kind === 'receipt' && tx.unit_cost != null ? fmtMoney(tx.unit_cost * tx.qty) + ' · ' : ''}{fmtTime(tx.created_at)}{tx.created_by_name ? ` · ${tx.created_by_name}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoresEntryPage;
